import { useState, useRef, useEffect } from 'react'
import { Sparkles, Brain, Code2, Bug, Send, Loader2, AlertCircle, Key } from 'lucide-react'
import { sendMessage } from '../services/claudeApi'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

interface Bot {
  id: string
  name: string
  icon: React.ElementType
  tagline: string
  systemPrompt: string
  examplePrompts: string[]
}

const BOTS: Bot[] = [
  {
    id: 'reasoner',
    name: 'Idea Reasoner',
    icon: Brain,
    tagline: 'Strategy & logic advisor for features and business decisions',
    systemPrompt: 'You are a strategic advisor for a digital marketing agency ERP called Clickbait. Your role is to think through feature ideas, business decisions, and product strategy. Be concise, sharp, and practical. Ask ONE clarifying question if needed. Keep responses under 150 words. Always end with: "Ready to build? Say \'Build it\' or refine the idea."',
    examplePrompts: [
      'Should invoices auto-generate at month end?',
      'What if I add a client health score?',
      'Help me plan a reporting module',
      'Should I add a time tracker?',
    ],
  },
  {
    id: 'builder',
    name: 'Code Builder',
    icon: Code2,
    tagline: 'Generates production-ready React/TypeScript/Tailwind code',
    systemPrompt: 'You are a senior full-stack developer building Clickbait ERP — a React + TypeScript + TailwindCSS application for a digital marketing agency in India. Write production-ready code only. State what you\'re building in 1 sentence. Use the gold/black design system (primary: #ffc600, bg: #000, surface: #0f0f0f). Output complete code blocks, never scaffolds or placeholders.',
    examplePrompts: [
      'Build a client health score card component',
      'Create a time tracker with start/stop',
      'Add an export to PDF button for invoices',
      'Build a monthly P&L summary widget',
    ],
  },
  {
    id: 'debugger',
    name: 'Live Debugger',
    icon: Bug,
    tagline: 'Identifies and fixes bugs in your code instantly',
    systemPrompt: 'You are a debugging expert for React/TypeScript applications. When given code or an error: 1) State the bug in ONE line: "Bug: [what] on line [X]". 2) Explain WHY in 2 sentences max. 3) Provide the complete fixed code block. 4) End with: cause + how to prevent it. Never refactor unrelated code. If multiple bugs exist, list all first then fix one by one.',
    examplePrompts: [
      'TypeError: Cannot read properties of undefined reading \'map\'',
      'My invoice total isn\'t calculating correctly',
      'The sidebar isn\'t rendering on mobile',
      'React infinite re-render loop in useEffect',
    ],
  },
]

function BotChat({ bot, apiKey }: { bot: Bot; apiKey: string }) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const Icon = bot.icon

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const send = async (text: string) => {
    if (!text.trim() || loading) return
    const userMsg: Message = { role: 'user', content: text.trim() }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages)
    setInput('')
    setLoading(true)
    setError(null)
    try {
      const reply = await sendMessage(apiKey, bot.systemPrompt, newMessages)
      setMessages(prev => [...prev, { role: 'assistant', content: reply }])
    } catch (e: any) {
      setError(e.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Bot header */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl mb-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,198,0,0.12)' }}>
          <Icon size={18} style={{ color: '#ffc600' }} />
        </div>
        <div>
          <p className="text-sm font-bold text-white">{bot.name}</p>
          <p className="text-xs" style={{ color: '#a0a0a0' }}>{bot.tagline}</p>
        </div>
      </div>

      {/* Example prompts */}
      {messages.length === 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {bot.examplePrompts.map(p => (
            <button key={p} onClick={() => send(p)}
              className="text-xs px-3 py-1.5 rounded-lg transition-all text-left"
              style={{ background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#ffc600'; e.currentTarget.style.borderColor = 'rgba(255,198,0,0.35)' }}
              onMouseLeave={e => { e.currentTarget.style.color = '#a0a0a0'; e.currentTarget.style.borderColor = 'rgba(255,198,0,0.12)' }}>
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-4 pr-1">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="mr-2 mt-1 w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,198,0,0.12)' }}>
                <Icon size={13} style={{ color: '#ffc600' }} />
              </div>
            )}
            <div className="max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed"
              style={msg.role === 'user'
                ? { background: '#1a1a1a', color: '#fff', border: '1px solid rgba(255,198,0,0.25)' }
                : { background: '#0f0f0f', color: '#e0e0e0', border: '1px solid rgba(255,198,0,0.12)' }}>
              <pre className="whitespace-pre-wrap font-dm" style={{ fontFamily: 'inherit' }}>{msg.content}</pre>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="mr-2 mt-1 w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,198,0,0.12)' }}>
              <Icon size={13} style={{ color: '#ffc600' }} />
            </div>
            <div className="rounded-xl px-4 py-3" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
              <Loader2 size={16} className="animate-spin" style={{ color: '#ffc600' }} />
            </div>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ background: 'rgba(204,51,0,0.1)', border: '1px solid rgba(204,51,0,0.3)', color: '#cc3300' }}>
            <AlertCircle size={14} />{error}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2 items-end">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input) } }}
          placeholder={`Ask ${bot.name}...`}
          rows={2}
          className="flex-1 resize-none rounded-xl px-4 py-3 text-sm outline-none placeholder-gray-600 text-white"
          style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)', lineHeight: '1.5' }}
          onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
          onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')}
        />
        <button
          onClick={() => send(input)}
          disabled={loading || !input.trim()}
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40"
          style={{ background: '#ffc600' }}>
          {loading ? <Loader2 size={16} className="animate-spin text-black" /> : <Send size={16} color="#000" />}
        </button>
      </div>
    </div>
  )
}

const LS_KEY = 'erp_anthropic_key'
const envKey = import.meta.env.VITE_ANTHROPIC_API_KEY || ''
// Treat placeholder value (not a real key) as empty
const isPlaceholder = !envKey || envKey.startsWith('your_') || !envKey.startsWith('sk-')

function getInitialKey(): string {
  try { return localStorage.getItem(LS_KEY) || (isPlaceholder ? '' : envKey) } catch { return '' }
}

export default function AISuite() {
  const [activeBot, setActiveBot] = useState(BOTS[0].id)
  const [apiKey, setApiKey] = useState(getInitialKey)
  const [showKeyInput, setShowKeyInput] = useState(() => !getInitialKey())

  const saveKey = () => {
    const trimmed = apiKey.trim()
    try {
      if (trimmed) localStorage.setItem(LS_KEY, trimmed)
      else         localStorage.removeItem(LS_KEY)
    } catch {}
    setShowKeyInput(false)
  }

  const currentBot = BOTS.find(b => b.id === activeBot)!

  return (
    <div className="p-6 min-h-full flex flex-col" style={{ background: '#000', height: 'calc(100vh - 0px)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Sparkles size={20} style={{ color: '#ffc600' }} />
          <h1 className="text-2xl font-bold font-syne text-white">AI Suite</h1>
        </div>
        <button
          onClick={() => setShowKeyInput(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all"
          style={{ background: '#0f0f0f', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
          <Key size={12} />{apiKey ? 'Change API Key' : 'Set API Key'}
        </button>
      </div>

      {/* API Key input */}
      {showKeyInput && (
        <div className="flex gap-2 mb-4">
          <input
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="sk-ant-..."
            className="flex-1 px-4 py-2 rounded-lg text-sm outline-none text-white placeholder-gray-600"
            style={{ background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }}
            onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')}
            onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')}
          />
          <button onClick={saveKey} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: '#ffc600', color: '#000' }}>
            Save
          </button>
        </div>
      )}

      {/* Bot tabs */}
      <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)', width: 'fit-content' }}>
        {BOTS.map(bot => {
          const Icon = bot.icon
          const isActive = activeBot === bot.id
          return (
            <button key={bot.id} onClick={() => setActiveBot(bot.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={isActive
                ? { background: '#ffc600', color: '#000' }
                : { background: 'transparent', color: '#a0a0a0' }}>
              <Icon size={14} />{bot.name}
            </button>
          )
        })}
      </div>

      {/* Chat area */}
      <div className="flex-1 min-h-0 rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)', display: 'flex', flexDirection: 'column' }}>
        {!apiKey ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Key size={32} style={{ color: '#555' }} />
            <p className="text-sm" style={{ color: '#a0a0a0' }}>Enter your Anthropic API key above to start chatting</p>
          </div>
        ) : (
          <BotChat key={activeBot} bot={currentBot} apiKey={apiKey} />
        )}
      </div>
    </div>
  )
}
